<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ $t("platform_statement.upload") }}
                    </h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>

                <div class="col-lg-6 col-5 text-right">
                    <base-button size="md" type="secondary" @click="handleBack">
                        <i class="fa fa-arrow-left mr-2"></i>{{ $t("back") }}
                    </base-button>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <div class="row justify-content-center">
                <div class="col-lg-8">
                    <card>
                        <template slot="header">
                            <h3 class="mb-0">{{ $t("platform_statement.upload_file") }}</h3>
                        </template>

                        <!-- Step 1: Select Platform -->
                        <div v-if="currentStep === 0">
                            <h5 class="mb-3">{{ $t("platform_statement.select_platform") }}</h5>
                            <div class="row">
                                <div class="col-md-4" v-for="platform in PlatformTable" :key="platform.id">
                                    <card
                                        :class="{'border-primary': selectedPlatform === platform.id}"
                                        class="platform-card cursor-pointer"
                                        @click.native="selectPlatform(platform.id)"
                                    >
                                        <div class="text-center">
                                            <div class="platform-icon mb-2">
                                                <i class="fa fa-shopping-bag fa-3x"
                                                   :class="{'text-primary': selectedPlatform === platform.id}"></i>
                                            </div>
                                            <h4>{{ platform.name }}</h4>
                                            <p class="text-muted">{{ $t("platform_statement.upload_file") }}</p>
                                        </div>
                                    </card>
                                </div>
                            </div>

                            <div class="text-right mt-4">
                                <base-button
                                    type="primary"
                                    :disabled="!selectedPlatform"
                                    @click="currentStep = 1"
                                >
                                    {{ $t("continue") }}
                                </base-button>
                            </div>
                        </div>

                        <!-- Step 2: Upload File -->
                        <div v-if="currentStep === 1">
                            <h5 class="mb-3">{{ $t("platform_statement.upload_file") }}</h5>
                            <p class="text-muted">{{ getPlatformName(selectedPlatform) }}</p>

                            <div class="upload-area">
                                <div class="form-group">
                                    <div class="custom-file">
                                        <input
                                            type="file"
                                            class="custom-file-input"
                                            id="fileInput"
                                            accept=".xlsx,.xls"
                                            @change="handleFileChange"
                                        />
                                        <label class="custom-file-label" for="fileInput">
                                            {{ selectedFile ? selectedFile.name : $t("platform_statement.drag_drop_file") }}
                                        </label>
                                    </div>
                                    <small class="form-text text-muted">
                                        {{ $t("platform_statement.supported_formats") }}<br/>
                                        {{ $t("platform_statement.max_file_size") }}
                                    </small>
                                </div>

                                <div v-if="selectedFile" class="mt-3">
                                    <card>
                                        <div class="row">
                                            <div class="col-md-6">
                                                <p><strong>{{ $t("name") }}:</strong> {{ selectedFile.name }}</p>
                                            </div>
                                            <div class="col-md-6">
                                                <p><strong>{{ $t("size") }}:</strong> {{ formatFileSize(selectedFile.size) }}</p>
                                            </div>
                                        </div>
                                    </card>
                                </div>
                            </div>

                            <div class="text-right mt-4">
                                <base-button
                                    type="secondary"
                                    @click="currentStep = 0"
                                >
                                    {{ $t("back") }}
                                </base-button>
                                <base-button
                                    type="primary"
                                    :disabled="!selectedFile"
                                    :loading="uploading"
                                    @click="handleUpload"
                                >
                                    {{ $t("platform_statement.upload_file") }}
                                </base-button>
                            </div>
                        </div>

                        <!-- Step 3: Success -->
                        <div v-if="currentStep === 2">
                            <div class="text-center py-5">
                                <i class="fa fa-check-circle fa-5x text-success mb-4"></i>
                                <h3>{{ $t("platform_statement.upload_success") }}</h3>

                                <card v-if="uploadResult" class="mt-4">
                                    <div class="row">
                                        <div class="col-md-6">
                                            <p><strong>{{ $t("platform_statement.statement_number") }}:</strong> {{ uploadResult.statement_number }}</p>
                                        </div>
                                        <div class="col-md-6">
                                            <p><strong>{{ $t("platform_statement.total_orders") }}:</strong> {{ formatNumber(uploadResult.total_orders) }}</p>
                                        </div>
                                        <div class="col-md-6">
                                            <p><strong>{{ $t("platform_statement.total_sales_amount") }}:</strong> {{ formatCurrency(uploadResult.total_sales_amount) }}</p>
                                        </div>
                                        <div class="col-md-6">
                                            <p><strong>{{ $t("platform_statement.net_amount") }}:</strong> {{ formatCurrency(uploadResult.net_amount) }}</p>
                                        </div>
                                    </div>
                                </card>

                                <div class="mt-4">
                                    <base-button
                                        type="info"
                                        @click="handleViewDetail"
                                        v-if="uploadResult"
                                    >
                                        {{ $t("platform_statement.view_details") }}
                                    </base-button>
                                    <base-button
                                        type="primary"
                                        @click="handleBack"
                                    >
                                        {{ $t("platform_statement.back_to_list") }}
                                    </base-button>
                                </div>
                            </div>
                        </div>
                    </card>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import swal from "sweetalert2";
import { mapGetters, mapActions } from "vuex";

export default {
    components: {
        RouteBreadCrumb
    },
    computed: {
        ...mapGetters({
            PlatformTable: "PlatformStatement/getPlatformTable"
        })
    },
    data() {
        return {
            currentStep: 0,
            selectedPlatform: null,
            selectedFile: null,
            uploading: false,
            uploadResult: null
        };
    },
    methods: {
        ...mapActions({
            uploadStatement: "PlatformStatement/uploadStatement"
        }),
        selectPlatform(platformId) {
            this.selectedPlatform = platformId;
        },
        handleFileChange(event) {
            const file = event.target.files[0];
            if (!file) return;

            // Validate file type
            const validTypes = [
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                "application/vnd.ms-excel"
            ];
            const validExtensions = [".xlsx", ".xls"];
            const fileExtension = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();

            if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
                swal.fire({
                    title: this.$t("error"),
                    text: this.$t("platform_statement.supported_formats"),
                    icon: "error",
                    confirmButtonClass: "btn btn-danger btn-fill",
                    buttonsStyling: false
                });
                event.target.value = "";
                return;
            }

            // Validate file size (50MB)
            const maxSize = 50 * 1024 * 1024;
            if (file.size > maxSize) {
                swal.fire({
                    title: this.$t("error"),
                    text: this.$t("platform_statement.max_file_size"),
                    icon: "error",
                    confirmButtonClass: "btn btn-danger btn-fill",
                    buttonsStyling: false
                });
                event.target.value = "";
                return;
            }

            this.selectedFile = file;
        },
        async handleUpload() {
            if (!this.selectedFile || !this.selectedPlatform) {
                return;
            }

            this.uploading = true;

            const formData = new FormData();
            formData.append("file", this.selectedFile);
            formData.append("platform_id", this.selectedPlatform);

            try {
                const response = await this.uploadStatement(formData);
                this.uploadResult = response.data.data;
                this.currentStep = 2;
            } catch (error) {
                console.error(error);
            } finally {
                this.uploading = false;
            }
        },
        handleBack() {
            this.$router.push("/backoffice/platform-statement");
        },
        handleViewDetail() {
            if (this.uploadResult && this.uploadResult.statement_id) {
                this.$router.push(`/backoffice/platform-statement/${this.uploadResult.statement_id}`);
            }
        },
        getPlatformName(platformId) {
            const platform = this.PlatformTable.find(p => p.id === platformId);
            return platform ? platform.name : "";
        },
        formatFileSize(bytes) {
            if (bytes === 0) return "0 Bytes";
            const k = 1024;
            const sizes = ["Bytes", "KB", "MB", "GB"];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
        },
        formatCurrency(amount) {
            return "฿" + parseFloat(amount || 0).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        },
        formatNumber(num) {
            return parseInt(num || 0).toLocaleString();
        }
    }
};
</script>

<style scoped>
.platform-card {
    cursor: pointer;
    transition: all 0.3s;
}

.platform-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.border-primary {
    border: 2px solid #5e72e4 !important;
    background-color: #f8f9fe;
}

.upload-area {
    min-height: 200px;
}

.cursor-pointer {
    cursor: pointer;
}
</style>
